import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { useState } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { apiGet } from "@/lib/api-client";
import { configKeys } from "@/lib/query";
import { configUserArraySchema } from "@/schemas/config";

export function ConfigUsersPage() {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);

  const searchParam = debouncedSearch ? `?search=${encodeURIComponent(debouncedSearch)}` : "";

  const {
    data: users,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: configKeys.users({ search: debouncedSearch }),
    queryFn: () => apiGet(`/api/config/users${searchParam}`, configUserArraySchema),
  });

  if (isError) {
    return (
      <div>
        <h1 className="font-display text-3xl font-bold">Users</h1>
        <ErrorState message="Couldn't load users." onRetry={() => refetch()} />
      </div>
    );
  }

  return (
    <div>
      <div className="rounded-md border bg-card p-4">
        <h1 className="font-display text-3xl font-bold">Users</h1>
        <p className="mt-1 text-muted-foreground">
          Browse Active Directory users, departments, and status.
        </p>
      </div>

      <div className="mt-6 flex items-center gap-2">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search users..."
            className="pl-9 bg-card"
          />
        </div>
      </div>

      <Card className="mt-4">
        <CardHeader />
        <CardContent>
          {isLoading ? (
            <p className="py-8 text-center text-muted-foreground">Loading users...</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-3 pr-4 font-medium">Account Name</th>
                    <th className="pb-3 pr-4 font-medium">Name</th>
                    <th className="pb-3 pr-4 font-medium">Email</th>
                    <th className="pb-3 pr-4 font-medium">Department</th>
                    <th className="pb-3 font-medium">Title</th>
                  </tr>
                </thead>
                <tbody>
                  {users?.map((user) => (
                    <tr key={user.id} className="border-b">
                      <td className="py-3 pr-4 font-mono">{user.samAccountName}</td>
                      <td className="py-3 pr-4">{user.name}</td>
                      <td className="py-3 pr-4">{user.email ?? "—"}</td>
                      <td className="py-3 pr-4">{user.department ?? "—"}</td>
                      <td className="py-3">{user.title ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {users?.length === 0 && (
                <p className="py-8 text-center text-muted-foreground">
                  {debouncedSearch
                    ? `No users found matching "${debouncedSearch}".`
                    : "No users found."}
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
